



















































































































import {computed, defineComponent, PropType, ref} from "@vue/composition-api";
import ProjectLayout from "@tool/Pages/App/Project/layout/ProjectLayout.vue";
import {
    CrumbPage,
    PARAMETER_TYPES,
    ParameterEntityInterface,
    ParameterType,
    ProjectEntityInterface
} from "@tool/tool.types";
import {capitalize, validateState} from "@base/base.helpers";
import ThemeBlock from "@base/Components/theme/ThemeBlock.vue";
import {ResourceCollectionInterface, WithProps} from "@base/base.types";
import useInertiaTable from "@tool/Helpers/useInertiaTable";
import InertiaDatatable from "@base/Components/InertiaDatatable.vue";
import DisplayBoolean from "@base/Components/DisplayBoolean.vue";
import useFormHelper from "@tool/Helpers/useFormHelper";
import FormError from "@tool/Components/FormError.vue";
import useVuelidate from "@vuelidate/core";
import {required} from "@vuelidate/validators";
import {BvModalEvent} from "bootstrap-vue/src/components/modal";
import moment from "moment";
import BlockMessage from "@base/Components/BlockMessage.vue";
import {useToast} from "vue-toastification/composition";
import useAxios from "@tool/Helpers/useAxios";
import {useInertia} from "@tool/Helpers/useInertia";
import ParameterNameGenerator from "@tool/Helpers/ParameterNameGenerator";

export default defineComponent({
    components: {
        FormError,
        ProjectLayout,
        ThemeBlock,
        InertiaDatatable,
        DisplayBoolean,
        BlockMessage,
    },
    props: {
        project: {
            type: Object as PropType<ProjectEntityInterface>,
            required: true
        },
        type: {
            type: String as PropType<ParameterType>,
            required: true
        },
        parameters: {
            type: Object as PropType<ResourceCollectionInterface<WithProps<ParameterEntityInterface, 'user'>>>,
            required: true
        }
    },
    setup(props) {

        const toast = useToast();

        const name = capitalize(props.type.replace('utm_', ''));

        const extraBreadcrumbs: CrumbPage[] = [{
            name: 'UTM Parameters',
        }];

        const tabItems = Object.keys(PARAMETER_TYPES).map((parameterType: string) => {
            const pluralName = parameterType.replace('utm_', '') + 's';
            return {
                name: capitalize(pluralName),
                classes: {
                    active: parameterType === props.type,
                    'nav-link': true
                },
                href: route('app.project.parameters.' + pluralName + '.index', props.project.slug)
            }
        });


        const table = useInertiaTable(props.parameters, [
            {
                key: 'name',
                label: 'Name'
            },
            {
                key: 'active',
                label: 'Suggest in the builder'
            },
            {
                key: 'clicks',
                label: 'Lifetime Clicks'
            },
            {
                key: 'actions',
                label: 'Actions',
                class: 'actions'
            },
        ]);

        const modalState = ref(false);

        const modalMode = ref<'create' | 'edit'>('create');

        const modalTitle = computed(() => {
            return (modalMode.value === 'create' ? 'Add new ' : 'Edit ') + name + ' Parameter';
        });

        const modalOkTitle = computed(() => {
            return modalMode.value === 'create' ? 'Add' : 'Save';
        });

        const modalOkIsDisabled = computed(() => {
            return modalForm.value.processing || modalFormValidator.value.$invalid;
        });

        interface FormData {
            id: string;
            name: string;
            active: boolean;
            notes: string | null;
            created_by: string;
            created_on: string;
        }

        const modalForm = useFormHelper<FormData>({
            id: '',
            name: '',
            active: true,
            notes: '',
            created_by: '',
            created_on: '',
        });

        const modalFormValidator = useVuelidate({
            name: {
                required
            }
        }, modalForm.value.data);

        const modalFormHandle = async (event: BvModalEvent) => {
            event.preventDefault();

            if (modalMode.value === 'create') {

                const response = await modalForm.value.submit((data) => {
                    return useAxios().post<ParameterEntityInterface>(route('app.project.parameters.store', {
                        tool_project_entity: props.project.slug,
                    }), {
                        name: generatedName.value,
                        active: data.active,
                        notes: data.notes,
                        type: props.type,
                    });
                });

                if (response.success) {
                    useInertia().get(route(route().current(), route().params), {}, {
                        preserveScroll: true,
                        onSuccess() {
                            toast.success('Parameter was successfully added to your project!');
                        }
                    });
                }

                return;
            }

            const {success: successResponse} = await modalForm.value.submit((data) => {
                return useAxios().put<ParameterEntityInterface>(route('app.project.parameters.update', {
                    tool_project_entity: props.project.slug,
                    tool_parameter_entity: modalForm.value.data.id
                }), {
                    active: data.active,
                    notes: data.notes,
                });
            });

            if (successResponse) {
                useInertia().get(route(route().current(), route().params), {}, {
                    // Because it changes order anyways
                    preserveScroll: false,
                    onSuccess() {
                        toast.success('Parameter was successfully updated!');
                        modalState.value = false;
                    }
                });
            }

        }

        const modalOpen = (parameter: WithProps<ParameterEntityInterface, 'user'> | null) => {
            modalMode.value = parameter ? 'edit' : 'create';

            if (parameter) {
                modalForm.value.data.id = parameter.id;
                modalForm.value.data.name = parameter.name;
                modalForm.value.data.active = parameter.active;
                modalForm.value.data.notes = parameter.notes;
                modalForm.value.data.created_by = parameter.user.name_first;
                modalForm.value.data.created_on = moment(parameter.created_at).format('MMMM Do YYYY');
                modalFormValidator.value.$touch();
            } else {
                modalForm.value.reset();
            }

            modalState.value = true;
        }

        const modalOnClose = () => {
            modalFormValidator.value.$reset();
        }

        const generatedName = computed(() => {
            return ParameterNameGenerator(modalForm.value.data.name, props.project.builder_settings);
        })

        const modalShowCorrectedName = computed(() => {
            if (modalMode.value === 'edit') {
                return false;
            }

            return generatedName.value !== modalForm.value.data.name;
        });

        const statsForParameterHref = (param: ParameterEntityInterface) => {
            let params: any = {tool_project_entity: props.project.slug};

            params['filter_' + props.type] = param.id

            return route('app.project.reports.index', params);
        }


        return {
            name,
            extraBreadcrumbs,
            tabItems,
            table,

            // Modal
            modalState,
            modalMode,
            modalTitle,
            modalOkTitle,
            modalOkIsDisabled,

            modalForm,
            modalFormValidator,

            modalOpen,
            modalFormHandle,
            modalOnClose,
            validateState,

            generatedName,
            modalShowCorrectedName,

            statsForParameterHref,
        }
    }
})
